/**
 * Tenant Resolution Utility
 * Detects which school portal to load based on URL
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:5000';

/**
 * Get tenant identifier from current URL
 * Supports: subdomain, custom domain, query parameter, localStorage
 */
export function getTenantIdentifier() {
    if (typeof window === 'undefined') return null;

    const hostname = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);
    const schoolParam = urlParams.get('school');
    const schoolCodeParam = urlParams.get('schoolCode');

    // DEVELOPMENT MODE: localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const identifier = schoolCodeParam || schoolParam;
        if (identifier) {
            localStorage.setItem('dev_school_code', identifier.toUpperCase());
            return {
                mode: 'development',
                identifier: identifier.toUpperCase(),
                source: 'query',
            };
        }

        const savedSchool = localStorage.getItem('dev_school_code');
        if (savedSchool) {
            return {
                mode: 'development',
                identifier: savedSchool,
                source: 'localStorage',
            };
        }

        return {
            mode: 'development',
            identifier: null,
            source: 'manual',
            requiresSelection: true,
        };
    }

    const BASE_DOMAINS = (process.env.NEXT_PUBLIC_BASE_DOMAINS || '').split(',').map(d => d.trim()).filter(Boolean);
    const cleanHostname = hostname.toLowerCase().replace(/^www\./, '');
    const isBaseDomain = BASE_DOMAINS.includes(cleanHostname);

    // Identify if it's a subdomain of our SaaS 
    let detectedSubdomain = null;
    for (const base of BASE_DOMAINS) {
        if (cleanHostname.endsWith('.' + base)) {
            detectedSubdomain = cleanHostname.replace('.' + base, '');
            break;
        }
    }

    // CASE 1: Main SaaS Website
    if (isBaseDomain) {
        const identifier = schoolCodeParam || schoolParam;
        if (identifier) {
            return {
                mode: 'development',
                identifier: identifier.toUpperCase(),
                source: 'query',
            };
        }
        return {
            mode: 'main',
            identifier: null,
            source: 'none',
        };
    }

    // CASE 2: School Subdomain
    if (detectedSubdomain && !['www', 'api', 'admin', 'superadmin', 'dev', 'staging', 'connect'].includes(detectedSubdomain)) {
        return {
            mode: 'subdomain',
            identifier: detectedSubdomain,
            source: 'subdomain',
        };
    }

    // CASE 3: Custom Domain (e.g., myschool.com or www.myschool.com)
    // If it's a system subdomain of our SaaS, treat as main
    if (detectedSubdomain && ['www', 'api', 'admin', 'superadmin', 'dev', 'staging', 'connect'].includes(detectedSubdomain)) {
        return {
            mode: 'main',
            identifier: null,
            source: 'none',
        };
    }

    // Otherwise, treat as a custom domain
    return {
        mode: 'customDomain',
        identifier: cleanHostname,
        source: 'customDomain',
    };
}

/**
 * Resolve tenant information from backend
 */
export async function resolveTenant() {
    const tenantId = getTenantIdentifier();

    if (!tenantId || !tenantId.identifier) {
        return {
            resolved: false,
            requiresManualSelection: true,
            ...tenantId,
        };
    }

    try {
        const hostname = window.location.hostname;
        const params = new URLSearchParams({
            hostname,
            ...(tenantId.mode === 'development' && { school: tenantId.identifier }),
        });

        const fetchUrl = `${API_BASE_URL}/api/tenant/resolve?${params}`;
        console.log('🌐 [TenantResolver] Resolving tenant...', {
            mode: tenantId.mode,
            identifier: tenantId.identifier,
            url: fetchUrl
        });

        const response = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // 404 is an ORDINARY outcome, not a failure: this host (or ?school=
        // code) simply has no school behind it. Callers already handle
        // `resolved: false`, so return it rather than throwing — throwing
        // logged two red console errors and, under Next dev, raised a
        // full-screen error overlay for what is a normal empty result.
        // Mirrors the server-side rule in utils/serverTenant.js, which also
        // stays quiet on 404 and only logs other statuses.
        if (response.status === 404) {
            console.info('[TenantResolver] No school for this host:', {
                mode: tenantId.mode,
                identifier: tenantId.identifier,
            });
            return { resolved: false, notFound: true, ...tenantId };
        }

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ [TenantResolver] API Error Response:', {
                status: response.status,
                body: errorText
            });
            throw new Error(`Failed to resolve tenant: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ [TenantResolver] Resolution successful:', data);

        const isRootPath = window.location.pathname === '/' || window.location.pathname === '';
        const isSubdomain = tenantId.mode === 'subdomain' || tenantId.mode === 'customDomain';

        if (data.success && isSubdomain && isRootPath) {
            data.tenant.shouldRedirectToLogin = true;
        }

        return {
            resolved: true,
            ...data.tenant,
            schoolCode: data.tenant.schoolCode,
            detectionMode: tenantId.mode,
            detectionSource: tenantId.source,
        };
    } catch (error) {
        console.error('[TenantResolver] Failed to resolve tenant:', error);
        return {
            resolved: false,
            error: error.message,
            ...tenantId,
        };
    }
}

export function clearDevSchool() {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('dev_school_code');
        const url = new URL(window.location.href);
        url.searchParams.delete('school');
        url.searchParams.delete('schoolCode');
        window.location.href = url.toString();
    }
}

export function setDevSchool(schoolCode) {
    if (typeof window !== 'undefined') {
        localStorage.setItem('dev_school_code', schoolCode.toUpperCase());
        const url = new URL(window.location.href);
        url.searchParams.set('school', schoolCode.toUpperCase());
        window.location.href = url.toString();
    }
}

export function getCurrentTenantContext() {
    return getTenantIdentifier();
}


export function getBaseSaaSDomain() {
    const BASE_DOMAINS = (process.env.NEXT_PUBLIC_BASE_DOMAINS || '').split(',').map(d => d.trim()).filter(Boolean);

    if (typeof window === 'undefined') return BASE_DOMAINS[0] || '';

    const hostname = window.location.hostname;

    // Look for exact match or parent match
    const cleanHostname = hostname.toLowerCase().replace('www.', '');

    // Check if current hostname is one of the base domains or a subdomain of it
    for (const domain of BASE_DOMAINS) {
        if (cleanHostname === domain || cleanHostname.endsWith('.' + domain)) {
            return domain;
        }
    }

    // Fallback: Use the first domain in our config if on localhost/unknown.
    // `|| ''` matches the SSR branch above: NEXT_PUBLIC_BASE_DOMAINS is
    // build-time inlined and can be absent on a build host, and callers that
    // build absolute URLs from this would otherwise emit "https://undefined".
    return BASE_DOMAINS[0] || '';
}
